/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useState } from 'react';
import { VelocityData } from '@shared/types/dashboard';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Activity, TrendingUp } from 'lucide-react';

interface VelocityGraphProps {
  data: VelocityData;
  isLoading?: boolean;
}

type MetricView = 'count' | 'effort' | 'both';

export function VelocityGraph({ data, isLoading }: VelocityGraphProps) {
  const [metricView, setMetricView] = useState<MetricView>('both');

  // Transform data for recharts
  const chartData = data.weeks.map((week, index) => {
    const dataPoint: any = { week };
    
    // Add data for each product
    data.products.forEach(product => {
      if (metricView === 'count' || metricView === 'both') {
        dataPoint[`${product.productName} (Count)`] = product.completedCount[index];
      }
      if (metricView === 'effort' || metricView === 'both') {
        dataPoint[`${product.productName} (Effort)`] = product.effortPoints[index];
      }
    });
    
    return dataPoint;
  });

  // Generate colors for products
  const colors = [
    '#0066cc', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', 
    '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1'
  ];

  if (isLoading) {
    return (
      <div style={{
        background: '#fff',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '20px',
        height: '400px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', color: '#999' }}>
          <Activity size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p style={{ margin: 0 }}>Loading velocity data...</p>
        </div>
      </div>
    );
  }

  if (data.products.length === 0) {
    return (
      <div style={{
        background: '#fff',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '20px',
        height: '400px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', color: '#999' }}>
          <TrendingUp size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
          <p style={{ margin: 0 }}>No velocity data available</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
            Complete some issues to see velocity trends
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '20px'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: '20px',
        paddingBottom: '12px',
        borderBottom: '2px solid #f0f0f0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TrendingUp size={20} style={{ marginRight: '8px', color: '#0066cc' }} />
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Velocity Trends</h3>
        </div>
        
        {/* Metric Toggle */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setMetricView('count')}
            style={{
              padding: '6px 12px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              background: metricView === 'count' ? '#0066cc' : '#fff',
              color: metricView === 'count' ? '#fff' : '#333',
              fontSize: '13px',
              cursor: 'pointer',
              fontWeight: metricView === 'count' ? '600' : '400'
            }}
          >
            Count
          </button>
          <button
            onClick={() => setMetricView('effort')}
            style={{
              padding: '6px 12px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              background: metricView === 'effort' ? '#0066cc' : '#fff',
              color: metricView === 'effort' ? '#fff' : '#333',
              fontSize: '13px',
              cursor: 'pointer',
              fontWeight: metricView === 'effort' ? '600' : '400'
            }}
          >
            Effort
          </button>
          <button
            onClick={() => setMetricView('both')}
            style={{
              padding: '6px 12px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              background: metricView === 'both' ? '#0066cc' : '#fff',
              color: metricView === 'both' ? '#fff' : '#333',
              fontSize: '13px',
              cursor: 'pointer',
              fontWeight: metricView === 'both' ? '600' : '400'
            }}
          >
            Both
          </button>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e9e9e9" />
          <XAxis 
            dataKey="week" 
            tick={{ fontSize: 12 }}
            stroke="#666"
          />
          <YAxis 
            tick={{ fontSize: 12 }}
            stroke="#666"
            label={{ 
              value: metricView === 'count' ? 'Issues Completed' : metricView === 'effort' ? 'Effort Points' : 'Metrics', 
              angle: -90, 
              position: 'insideLeft',
              style: { fontSize: 12, fill: '#666' }
            }}
          />
          <Tooltip 
            contentStyle={{ 
              background: '#fff', 
              border: '1px solid #ddd', 
              borderRadius: '4px',
              fontSize: '12px'
            }}
          />
          <Legend 
            wrapperStyle={{ fontSize: '12px' }}
            iconType="circle"
          />
          
          {/* Render bars for each product and metric */}
          {data.products.map((product, index) => {
            const color = colors[index % colors.length];
            
            return (
              <React.Fragment key={product.productId}>
                {(metricView === 'count' || metricView === 'both') && (
                  <Bar
                    dataKey={`${product.productName} (Count)`}
                    fill={color}
                    opacity={metricView === 'both' ? 0.8 : 1}
                  />
                )}
                {(metricView === 'effort' || metricView === 'both') && (
                  <Bar
                    dataKey={`${product.productName} (Effort)`}
                    fill={color}
                    opacity={metricView === 'both' ? 0.6 : 1}
                  />
                )}
              </React.Fragment>
            );
          })}
        </BarChart>
      </ResponsiveContainer>

      {/* Summary */}
      <div style={{ 
        marginTop: '16px', 
        paddingTop: '12px', 
        borderTop: '1px solid #e9e9e9',
        display: 'flex',
        gap: '16px',
        flexWrap: 'wrap',
        fontSize: '12px',
        color: '#666'
      }}>
        <div>
          <strong>Products:</strong> {data.products.length}
        </div>
        <div>
          <strong>Weeks:</strong> {data.weeks.length}
        </div>
        <div>
          <strong>Total Completed:</strong>{' '}
          {data.products.reduce((sum, p) => sum + p.completedCount.reduce((a, b) => a + b, 0), 0)} issues
        </div>
        <div>
          <strong>Total Effort:</strong>{' '}
          {data.products.reduce((sum, p) => sum + p.effortPoints.reduce((a, b) => a + b, 0), 0)} points
        </div>
      </div>
    </div>
  );
}

